import * as moment from "moment-timezone";
import type { ReporteChequeo } from "../models/ReporteChequeo.ts";
import type { BranchReportMeta } from "./payrollBranchMeta.ts";
import { parseTimeToMinutes } from "./payrollBranchMeta.ts";
import {
  effectiveNightMetaForPartner,
  isPartnerNocturnoShift,
  partnerShiftFromIncidentSchedule,
} from "./nightScheduleEligibility.ts";
import {
  getReportDateKey,
  operationalDayKeyForReportCheck,
} from "./operationalDayReporteChequeo.ts";
import {
  buildVisitRowsForPartnerBranch,
  isSameCalendarDayDayShiftOrdered,
} from "./visitPairingReporteChequeo.ts";

const MORNING_MAX_MINUTES = 10 * 60;
const EVENING_MIN_MINUTES = 12 * 60;

function splitScheduleTime(value?: string | null, part: "start" | "end" = "start"): string {
  if (!value) return "";
  const parts = String(value).split(" - ").map((p) => p.trim());
  if (part === "start") {
    return parts[0] || "";
  }
  return parts[1] || parts[0] || "";
}

/**
 * Horario programado para UI (TableChequeo / incidencias / nómina).
 * Con horario nocturno, si en BD quedó invertido (05:38 – 21:08), se muestra 21:08 – 05:38.
 */
export function resolveScheduledTimesForDisplay(
  startTime?: string | null,
  endTime?: string | null,
  nightScheduleEnabled = false
): { entrada: string; salida: string } {
  let entrada = splitScheduleTime(startTime, "start");
  let salida = splitScheduleTime(endTime, "end");

  if (!nightScheduleEnabled || !entrada || !salida) {
    return { entrada, salida };
  }

  const entradaMin = parseTimeToMinutes(entrada);
  const salidaMin = parseTimeToMinutes(salida);

  const looksInverted =
    (entradaMin < MORNING_MAX_MINUTES && salidaMin >= EVENING_MIN_MINUTES) ||
    (entradaMin >= EVENING_MIN_MINUTES &&
      salidaMin < MORNING_MAX_MINUTES &&
      salidaMin < entradaMin);

  if (looksInverted) {
    return { entrada: salida, salida: entrada };
  }

  return { entrada, salida };
}

type CheckLike = {
  id?: number;
  checkTime?: string;
  checkDate?: string | Date;
} | null | undefined;

type IncidentScheduleLike = {
  id?: number;
  exactDate?: string | Date;
  partner?: { id?: number; candidate?: { shift?: { name?: string } } };
  branch?: { id?: number };
  branchId?: number;
  shift?: string;
  assistanceCheck?: CheckLike;
  assistanceCheckout?: CheckLike;
  checks?: CheckLike[];
};

type IncidentRecordLike = {
  checkIn?: CheckLike;
  checkOut?: CheckLike;
  schedule?: IncidentScheduleLike;
};

/**
 * Alinea checadas reales al turno (entrada antes que salida en sentido laboral).
 */
export function normalizeRealChecksForDisplay(
  checkIn: CheckLike,
  checkOut: CheckLike,
  nightScheduleEnabled = false
): { checkIn: CheckLike; checkOut: CheckLike } {
  if (!nightScheduleEnabled || !checkIn?.checkTime || !checkOut?.checkTime) {
    return { checkIn, checkOut };
  }

  const inMin = parseTimeToMinutes(checkIn.checkTime);
  const outMin = parseTimeToMinutes(checkOut.checkTime);
  const crossDay =
    Boolean(checkIn.checkDate) &&
    Boolean(checkOut.checkDate) &&
    String(checkIn.checkDate) !== String(checkOut.checkDate);

  if (crossDay) {
    if (inMin < MORNING_MAX_MINUTES && outMin >= EVENING_MIN_MINUTES) {
      return { checkIn: checkOut, checkOut: checkIn };
    }
    return { checkIn, checkOut };
  }

  if (inMin > outMin && outMin < MORNING_MAX_MINUTES) {
    return { checkIn: checkOut, checkOut: checkIn };
  }

  if (inMin < MORNING_MAX_MINUTES && outMin >= EVENING_MIN_MINUTES) {
    return { checkIn: checkOut, checkOut: checkIn };
  }

  return { checkIn, checkOut };
}

export type ResolveIncidentRealChecksOptions = {
  branchMeta?: BranchReportMeta;
  shiftName?: string | null;
  /**
   * Jefe directo: si hay colaboradores Nocturno, usar el mayor corte entre
   * sucursales con horario nocturno (solo cuando la sucursal del colaborador
   * también tiene nocturno habilitado).
   */
  maxNightCutoffMinutes?: number;
};

function resolveNightMetaForIncident(
  record: IncidentRecordLike,
  options?: boolean | ResolveIncidentRealChecksOptions,
  legacyCutoffMinutes = 3 * 60
): BranchReportMeta {
  if (typeof options === "boolean") {
    return {
      nightScheduleEnabled: options,
      cutoffMinutes: legacyCutoffMinutes,
    };
  }

  const shiftName =
    options?.shiftName ?? partnerShiftFromIncidentSchedule(record?.schedule);
  const fromBranch = effectiveNightMetaForPartner(
    options?.branchMeta,
    shiftName
  );
  if (!fromBranch.nightScheduleEnabled) {
    return fromBranch;
  }

  const maxCutoff = options?.maxNightCutoffMinutes;
  if (maxCutoff != null && maxCutoff > 0 && isPartnerNocturnoShift(shiftName)) {
    return {
      nightScheduleEnabled: true,
      cutoffMinutes: maxCutoff,
    };
  }

  return fromBranch;
}

export function resolveIncidentRealChecks(
  record: IncidentRecordLike,
  options?: boolean | ResolveIncidentRealChecksOptions,
  legacyCutoffMinutes = 3 * 60
): { checkIn: CheckLike; checkOut: CheckLike } {
  const effectiveMeta = resolveNightMetaForIncident(
    record,
    options,
    legacyCutoffMinutes
  );
  const branchMeta =
    typeof options === "object" ? options?.branchMeta : undefined;
  const shiftName =
    typeof options === "object"
      ? options?.shiftName
      : partnerShiftFromIncidentSchedule(record?.schedule);

  if (effectiveMeta.nightScheduleEnabled) {
    const paired = resolveIncidentRealChecksFromOperationalVisits(
      record,
      branchMeta,
      shiftName ?? undefined,
      effectiveMeta.cutoffMinutes
    );
    if (paired.checkIn) {
      return paired;
    }
  }

  const dayShiftPair = resolveIncidentRealChecksFromSameCalendarDay(
    record,
    effectiveMeta.cutoffMinutes
  );
  if (dayShiftPair.checkIn) {
    return dayShiftPair;
  }

  const rawIn =
    record?.checkIn ?? record?.schedule?.assistanceCheck ?? null;
  const rawOut =
    record?.checkOut ?? record?.schedule?.assistanceCheckout ?? null;

  if (!effectiveMeta.nightScheduleEnabled && rawIn && rawOut) {
    const inDate = formatCheckDateYmd(rawIn.checkDate);
    const outDate = formatCheckDateYmd(rawOut.checkDate);
    const outMin = parseTimeToMinutes(String(rawOut.checkTime || ""));
    if (
      inDate &&
      outDate &&
      inDate !== outDate &&
      outMin >= effectiveMeta.cutoffMinutes
    ) {
      return { checkIn: rawIn, checkOut: null };
    }
  }

  return normalizeRealChecksForDisplay(
    rawIn,
    rawOut,
    effectiveMeta.nightScheduleEnabled
  );
}

/** Turno diurno en el mismo día calendario (p. ej. 08:06–18:16), como TableReporteChequeo. */
function resolveIncidentRealChecksFromSameCalendarDay(
  record: IncidentRecordLike,
  cutoffMinutes: number
): { checkIn: CheckLike; checkOut: CheckLike } {
  const schedule = record?.schedule;
  const partnerId = Number(schedule?.partner?.id);
  const branchId = Number(schedule?.branchId ?? schedule?.branch?.id);
  const operationDate = formatCheckDateYmd(schedule?.exactDate);
  const rawChecks = Array.isArray(schedule?.checks) ? schedule.checks : [];

  if (!partnerId || !branchId || !operationDate || rawChecks.length < 2) {
    return { checkIn: null, checkOut: null };
  }

  const sameDayChecks = rawChecks
    .filter(
      (check): check is NonNullable<CheckLike> =>
        check != null &&
        formatCheckDateYmd(check.checkDate) === operationDate
    )
    .sort((a, b) =>
      String(a.checkTime ?? "").localeCompare(String(b.checkTime ?? ""))
    );

  if (sameDayChecks.length < 2) {
    return { checkIn: null, checkOut: null };
  }

  const reportRows = sameDayChecks
    .map((check) =>
      incidentCheckToReporteChequeo(check, partnerId, branchId)
    )
    .filter((row): row is ReporteChequeo => row !== null);

  if (
    !isSameCalendarDayDayShiftOrdered(reportRows, cutoffMinutes) ||
    reportRows.length < 2
  ) {
    return { checkIn: null, checkOut: null };
  }

  const checkIn = sameDayChecks[0];
  const checkOut = sameDayChecks[sameDayChecks.length - 1];
  if (
    !checkIn ||
    !checkOut ||
    checkIn.id === checkOut.id ||
    String(checkIn.checkTime) === String(checkOut.checkTime)
  ) {
    return { checkIn: checkIn ?? null, checkOut: null };
  }

  return { checkIn, checkOut };
}

function formatCheckDateYmd(value: unknown): string {
  if (!value) return "";
  return moment.utc(value).format("YYYY-MM-DD");
}

function incidentCheckToReporteChequeo(
  check: CheckLike,
  partnerId: number,
  branchId: number,
  shiftName?: string
): ReporteChequeo | null {
  if (!check?.id || !check.checkTime) return null;
  const checkDate = formatCheckDateYmd(check.checkDate);
  if (!checkDate) return null;
  return {
    assistanceCheck_id: Number(check.id),
    assistanceCheck_checkDate: checkDate,
    assistanceCheck_checkTime: String(check.checkTime),
    candidate_shift_name: shiftName,
    partner_id: partnerId,
    branch_id: branchId,
    branch_name: "",
    person_firstName: "",
    person_lastName: "",
    person_secondLastName: "",
    position_name: "",
    assistanceCheck_coords: "",
    assistanceCheck_createdAt: "",
  };
}

/** Mismo pareo que TableReporteChequeo (visitas por día operativo). */
function resolveIncidentRealChecksFromOperationalVisits(
  record: IncidentRecordLike,
  branchMeta: BranchReportMeta | undefined,
  shiftName: string | undefined,
  cutoffMinutes: number
): { checkIn: CheckLike; checkOut: CheckLike } {
  const schedule = record?.schedule;
  const partnerId = Number(schedule?.partner?.id);
  const branchId = Number(schedule?.branchId ?? schedule?.branch?.id);
  const operationDate = formatCheckDateYmd(schedule?.exactDate);

  const rawChecks = Array.isArray(schedule?.checks) ? schedule.checks : [];
  if (!partnerId || !branchId || !operationDate || rawChecks.length === 0) {
    return { checkIn: null, checkOut: null };
  }

  const baseMeta: BranchReportMeta = branchMeta ?? {
    nightScheduleEnabled: true,
    cutoffMinutes,
  };

  const reportChecks = rawChecks
    .map((check) =>
      incidentCheckToReporteChequeo(check, partnerId, branchId, shiftName)
    )
    .filter((row): row is ReporteChequeo => row !== null);

  if (reportChecks.length === 0) {
    return { checkIn: null, checkOut: null };
  }

  const nextCalKey = moment
    .utc(operationDate, "YYYY-MM-DD")
    .add(1, "day")
    .format("YYYY-MM-DD");

  const checkCurrent = reportChecks.filter((row) => {
    const calKey = getReportDateKey(row.assistanceCheck_checkDate);
    return (
      calKey === operationDate &&
      operationalDayKeyForReportCheck(row, baseMeta, reportChecks) ===
        operationDate
    );
  });
  const checkNext = reportChecks.filter((row) => {
    const calKey = getReportDateKey(row.assistanceCheck_checkDate);
    return (
      calKey === nextCalKey &&
      operationalDayKeyForReportCheck(row, baseMeta, reportChecks) ===
        operationDate
    );
  });

  const visitRows = buildVisitRowsForPartnerBranch(
    checkCurrent,
    checkNext,
    branchId,
    partnerId,
    checkCurrent,
    checkNext,
    cutoffMinutes,
    true
  );

  if (!visitRows.length) {
    return { checkIn: null, checkOut: null };
  }

  const rawInId = Number(
    record?.checkIn?.id ?? schedule?.assistanceCheck?.id ?? 0
  );
  const rawOutId = Number(
    record?.checkOut?.id ?? schedule?.assistanceCheckout?.id ?? 0
  );

  let chosen = visitRows[0];
  if (rawInId > 0 || rawOutId > 0) {
    const byId = visitRows.find((row) => {
      const inId = Number(row.checkIn?.assistanceCheck_id ?? 0);
      const outId = Number(row.checkOut?.assistanceCheck_id ?? 0);
      return (
        (rawInId > 0 && inId === rawInId) ||
        (rawOutId > 0 && outId === rawOutId) ||
        (rawInId > 0 && outId === rawInId)
      );
    });
    if (byId) chosen = byId;
  } else {
    const evening = visitRows.find((row) => {
      const t = row.checkIn?.assistanceCheck_checkTime ?? "";
      return parseTimeToMinutes(t) >= 12 * 60;
    });
    if (evening) chosen = evening;
  }

  const mapBack = (reportCheck?: ReporteChequeo | null): CheckLike => {
    if (!reportCheck) return null;
    return {
      id: reportCheck.assistanceCheck_id,
      checkDate: reportCheck.assistanceCheck_checkDate,
      checkTime: reportCheck.assistanceCheck_checkTime,
    };
  };

  return {
    checkIn: mapBack(chosen.checkIn),
    checkOut: mapBack(chosen.checkOut ?? null),
  };
}
